// src/components/ProgressForm.js
import React, { useState } from 'react';
import api from '../api';
import { IoClose } from 'react-icons/io5';

const initialFormState = {
  date: new Date().toISOString().split('T')[0],
  weight_kg: '',
  body_fat_percentage: '',
  waist_cm: '',
  chest_cm: '',
  hips_cm: '',
  bicep_cm: '',
  thigh_cm: '',
  photo_url: '',
  notes: '',
};

const ProgressForm = ({ onClose, onLogCreated }) => {
  const [form, setForm] = useState(initialFormState);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Construir payload: omitir campos vacíos para enviar null implícito
    const payload = { date: form.date };
    const numericFields = [
      'weight_kg', 'body_fat_percentage', 'waist_cm',
      'chest_cm', 'hips_cm', 'bicep_cm', 'thigh_cm',
    ];
    numericFields.forEach((field) => {
      if (form[field] !== '') payload[field] = parseFloat(form[field]);
    });
    if (form.photo_url.trim()) payload.photo_url = form.photo_url.trim();
    if (form.notes.trim()) payload.notes = form.notes.trim();

    try {
      const response = await api.post('/me/progress', payload);
      onLogCreated(response.data);
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al guardar el registro.');
    } finally {
      setLoading(false);
    }
  };

  const inputClass =
    'w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white placeholder-gray-400 focus:outline-none focus:border-marizan-red text-sm';
  const labelClass = 'block text-gray-300 text-xs font-semibold uppercase tracking-wide mb-1';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 rounded-xl shadow-2xl w-full max-w-lg border border-gray-700 max-h-screen overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-center p-5 border-b border-gray-700">
          <h2 className="text-xl font-bold text-white">Registrar Progreso</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white transition-colors">
            <IoClose size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-5">
          {error && (
            <p className="bg-red-900 border border-red-700 text-red-200 text-sm px-4 py-2 rounded-lg">
              {error}
            </p>
          )}

          {/* Fecha */}
          <div>
            <label className={labelClass}>Fecha</label>
            <input type="date" name="date" value={form.date} onChange={handleChange} required className={inputClass} />
          </div>

          {/* Peso y % Grasa */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Peso (kg)</label>
              <input type="number" step="0.1" name="weight_kg" value={form.weight_kg} onChange={handleChange} placeholder="ej. 75.5" className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>% Grasa corporal</label>
              <input type="number" step="0.1" name="body_fat_percentage" value={form.body_fat_percentage} onChange={handleChange} placeholder="ej. 18.5" className={inputClass} />
            </div>
          </div>

          {/* Medidas */}
          <div>
            <p className={labelClass}>Medidas (cm) — opcionales</p>
            <div className="grid grid-cols-2 gap-3">
              {[
                { name: 'waist_cm', label: 'Cintura' },
                { name: 'chest_cm', label: 'Pecho' },
                { name: 'hips_cm', label: 'Caderas' },
                { name: 'bicep_cm', label: 'Bícep' },
                { name: 'thigh_cm', label: 'Muslo' },
              ].map(({ name, label }) => (
                <div key={name}>
                  <label className="block text-gray-400 text-xs mb-1">{label}</label>
                  <input
                    type="number"
                    step="0.1"
                    name={name}
                    value={form[name]}
                    onChange={handleChange}
                    placeholder="cm"
                    className={inputClass}
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Foto URL */}
          <div>
            <label className={labelClass}>URL de foto (opcional)</label>
            <input
              type="url"
              name="photo_url"
              value={form.photo_url}
              onChange={handleChange}
              placeholder="https://..."
              className={inputClass}
            />
          </div>

          {/* Notas */}
          <div>
            <label className={labelClass}>Notas</label>
            <textarea
              name="notes"
              value={form.notes}
              onChange={handleChange}
              rows={3}
              placeholder="¿Cómo te has sentido? ¿Algún cambio notable?"
              className={inputClass + ' resize-none'}
            />
          </div>

          {/* Botones */}
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 bg-gray-700 hover:bg-gray-600 text-white font-semibold py-2 rounded-lg transition-colors">
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 bg-marizan-red hover:bg-red-700 disabled:opacity-50 text-white font-semibold py-2 rounded-lg transition-colors"
            >
              {loading ? 'Guardando...' : 'Guardar Registro'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProgressForm;
